import React, { useEffect, useState, useRef } from 'react';
import { Box, Card, CardContent, Typography, Grid, Container, CircularProgress, IconButton, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList } from 'recharts';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import PageLayout from '../../components/PageLayout';
import { useTheme } from '@mui/material/styles';
import { colors } from '../../theme/theme';
import { BarChart as BarChartIcon, Public, QrCode, ChevronLeft, ChevronRight, Facebook } from '@mui/icons-material';
import { useAnalyticsSettings } from '../../hooks/useAnalyticsSettings';
import { AnalyticsChart } from './AnalyticsChart';
import ResponsiveTitle from '../../components/ResponsiveTitle';

interface DailyStats {
    date: string;
    visits: number;
    visits_logged_in?: number;
    visits_anonymous?: number;
    flyer_scans?: number;
    flyer_scans_logged_in?: number;
    flyer_scans_anonymous?: number;
    facebook_visits_logged_in?: number;
    facebook_visits_anonymous?: number;
    failed_logins?: number;
}

export const Analytics = () => {
    const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
    const [totalFlyerScans, setTotalFlyerScans] = useState(0);
    const [totalFlyerScansLoggedIn, setTotalFlyerScansLoggedIn] = useState(0);
    const [totalFacebookVisits, setTotalFacebookVisits] = useState(0);
    const [totalFacebookVisitsLoggedIn, setTotalFacebookVisitsLoggedIn] = useState(0);
    const [totalVisitsAllTime, setTotalVisitsAllTime] = useState(0);
    const [totalFailedLoginsAllTime, setTotalFailedLoginsAllTime] = useState(0);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { colors: customColors, updateColor } = useAnalyticsSettings();
    const colorInputRef = useRef<HTMLInputElement>(null);
    const [activeColorKey, setActiveColorKey] = useState<string | null>(null);

    const handleColorClick = (key: string) => {
        setActiveColorKey(key);
        if (colorInputRef.current) {
            colorInputRef.current.click();
        }
    };

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (activeColorKey) {
            // @ts-ignore
            updateColor(activeColorKey, e.target.value);
        }
    };

    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Fetch daily stats
                const dailyRef = collection(db, 'analytics_daily');
                const q = query(dailyRef, orderBy('date', 'desc'), limit(3650));
                const snapshot = await getDocs(q);

                const stats: DailyStats[] = [];
                let visitsSumAllTime = 0;
                let failedLoginsSumAllTime = 0;

                snapshot.forEach((doc) => {
                    const data = doc.data();

                    const visitsLoggedIn = Number(data.visits_logged_in) || 0;
                    const visitsAnonymous = Number(data.visits_anonymous) || 0;
                    const flyerScansLoggedIn = Number(data.flyer_scans_logged_in) || 0;
                    const flyerScansAnonymous = Number(data.flyer_scans_anonymous) || 0;
                    const facebookVisitsLoggedIn = Number(data.facebook_visits_logged_in) || 0;
                    const facebookVisitsAnonymous = Number(data.facebook_visits_anonymous) || 0;

                    // Legacy fallback: if breakdown is missing, assume it's "visits" (anonymous mostly)
                    const totalVisitsOld = Number(data.visits) || 0;

                    // We reconcile old and new data. 
                    const effectiveVisitsAnon = visitsAnonymous > 0 ? visitsAnonymous : (totalVisitsOld > 0 && visitsLoggedIn === 0 ? totalVisitsOld : 0);

                    const totalVisits = visitsLoggedIn + effectiveVisitsAnon;
                    const totalFlyerScans = flyerScansLoggedIn + flyerScansAnonymous;
                    const totalFacebookVisits = facebookVisitsLoggedIn + facebookVisitsAnonymous;

                    stats.push({
                        date: data.date,
                        visits: totalVisits,
                        visits_logged_in: visitsLoggedIn,
                        visits_anonymous: effectiveVisitsAnon,
                        flyer_scans: totalFlyerScans,
                        flyer_scans_logged_in: flyerScansLoggedIn,
                        flyer_scans_anonymous: flyerScansAnonymous,
                        facebook_visits_logged_in: facebookVisitsLoggedIn,
                        facebook_visits_anonymous: facebookVisitsAnonymous,
                        failed_logins: Number(data.failed_logins) || 0
                    });

                    visitsSumAllTime += totalVisits;
                    failedLoginsSumAllTime += (Number(data.failed_logins) || 0);
                });

                const sortedStats = stats.reverse();
                setDailyStats(sortedStats);
                setTotalVisitsAllTime(visitsSumAllTime);
                setTotalFailedLoginsAllTime(failedLoginsSumAllTime);

                // Fetch global campaign stats (Flyer)
                const campaignRef = doc(db, 'analytics_campaigns', 'flyer');
                const campaignSnap = await getDoc(campaignRef);
                if (campaignSnap.exists()) {
                    const cData = campaignSnap.data();
                    let totalScans = cData.total_scans || 0;
                    const totalScansCalc = (cData.total_scans_logged_in || 0) + (cData.total_scans_anonymous || 0);
                    if (totalScansCalc > totalScans) totalScans = totalScansCalc;

                    setTotalFlyerScans(totalScans);
                    setTotalFlyerScansLoggedIn(cData.total_scans_logged_in || 0);
                }

                // Fetch global campaign stats (Facebook)
                const facebookRef = doc(db, 'analytics_campaigns', 'facebook');
                const facebookSnap = await getDoc(facebookRef);
                if (facebookSnap.exists()) {
                    const fbData = facebookSnap.data();
                    const totalFbVisits = (fbData.total_visits_logged_in || 0) + (fbData.total_visits_anonymous || 0);
                    setTotalFacebookVisits(totalFbVisits);
                    setTotalFacebookVisitsLoggedIn(fbData.total_visits_logged_in || 0);
                }

            } catch (error) {
                console.error("Error fetching analytics:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <PageLayout containerWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </PageLayout>
        );
    }

    const chartStats = dailyStats
        .filter(stat => stat.date.startsWith(`${selectedYear}-`))
        .map(stat => ({
            ...stat,
            total: (stat.visits_logged_in || 0) + (stat.visits_anonymous || 0) +
                (stat.flyer_scans_logged_in || 0) + (stat.flyer_scans_anonymous || 0) +
                (stat.facebook_visits_logged_in || 0) + (stat.facebook_visits_anonymous || 0)
        }));

    // Calculate totals for the selected year
    const yearTotalVisits = chartStats.reduce((acc, curr) => acc + curr.visits, 0);
    const yearVisitsLoggedIn = chartStats.reduce((acc, curr) => acc + (curr.visits_logged_in || 0), 0);
    const yearVisitsAnonymous = chartStats.reduce((acc, curr) => acc + (curr.visits_anonymous || 0), 0);

    const yearTotalFlyerScans = chartStats.reduce((acc, curr) => acc + (curr.flyer_scans || 0), 0);
    const yearFlyerScansLoggedIn = chartStats.reduce((acc, curr) => acc + (curr.flyer_scans_logged_in || 0), 0);
    const yearFlyerScansAnonymous = chartStats.reduce((acc, curr) => acc + (curr.flyer_scans_anonymous || 0), 0);

    const yearTotalFacebookVisits = chartStats.reduce((acc, curr) => acc + ((curr.facebook_visits_logged_in || 0) + (curr.facebook_visits_anonymous || 0)), 0);
    const yearFacebookVisitsLoggedIn = chartStats.reduce((acc, curr) => acc + (curr.facebook_visits_logged_in || 0), 0);
    const yearFacebookVisitsAnonymous = chartStats.reduce((acc, curr) => acc + (curr.facebook_visits_anonymous || 0), 0);

    const handlePrevYear = () => setSelectedYear(prev => prev - 1);
    const handleNextYear = () => setSelectedYear(prev => prev + 1);

    return (
        <PageLayout containerWidth="lg">
            <Box sx={{ mb: 4, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: 4,
                    px: 3,
                    py: 1,
                    color: 'white',
                    ml: { xs: 9, md: 0 },
                    width: { xs: 'calc(100% - 90px)', md: 'auto' },
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}>
                    <BarChartIcon sx={{ fontSize: 32 }} />
                    <ResponsiveTitle variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        Tableau de Bord Analytique
                    </ResponsiveTitle>
                </Box>

                {/* Year Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'background.paper', p: 1, borderRadius: 2, boxShadow: 1 }}>
                    <IconButton onClick={handlePrevYear} size="small">
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', minWidth: 80, textAlign: 'center' }}>
                        {selectedYear}
                    </Typography>
                    <IconButton onClick={handleNextYear} size="small" disabled={selectedYear >= new Date().getFullYear()}>
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>

            {/* Hidden Color Picker */}
            <input
                type="color"
                ref={colorInputRef}
                style={{ display: 'none' }}
                onChange={handleColorChange}
            />

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Section Visites Web Directes */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderLeft: `6px solid ${theme.palette.primary.main}`, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -20, bottom: -20, opacity: 0.1, transform: 'rotate(-15deg)' }}>
                            <Public sx={{ fontSize: 180, color: theme.palette.primary.main }} />
                        </Box>
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Visites Web (Direct)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
                                <Typography variant="h3" component="div">
                                    {yearTotalVisits}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Total {selectedYear}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Connectés</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: customColors.web_connected, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        onClick={() => handleColorClick('web_connected')}
                                    >
                                        {yearVisitsLoggedIn}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Anonymes</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: customColors.web_anonymous, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        onClick={() => handleColorClick('web_anonymous')}
                                    >
                                        {yearVisitsAnonymous}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Section Scans Flyer */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderLeft: `6px solid ${theme.palette.secondary.main}`, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -20, bottom: -20, opacity: 0.1, transform: 'rotate(-15deg)' }}>
                            <QrCode sx={{ fontSize: 180, color: theme.palette.secondary.main }} />
                        </Box>
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Visites par QRCode
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
                                <Typography variant="h3" component="div">
                                    {yearTotalFlyerScans}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Total {selectedYear}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Connectés</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: customColors.flyer_connected, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        onClick={() => handleColorClick('flyer_connected')}
                                    >
                                        {yearFlyerScansLoggedIn}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Anonymes</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: customColors.flyer_anonymous, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        onClick={() => handleColorClick('flyer_anonymous')}
                                    >
                                        {yearFlyerScansAnonymous}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Section Facebook */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderLeft: `6px solid #1877F2`, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -20, bottom: -20, opacity: 0.1, transform: 'rotate(-15deg)' }}>
                            <Facebook sx={{ fontSize: 180, color: '#1877F2' }} />
                        </Box>
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Visites Facebook
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
                                <Typography variant="h3" component="div">
                                    {yearTotalFacebookVisits}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Total {selectedYear}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Connectés</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: customColors.facebook_connected || '#1877F2', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        onClick={() => handleColorClick('facebook_connected')}
                                    >
                                        {yearFacebookVisitsLoggedIn}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Anonymes</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ color: customColors.facebook_anonymous || '#89b6f8', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        onClick={() => handleColorClick('facebook_anonymous')}
                                    >
                                        {yearFacebookVisitsAnonymous}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Heatmap Removed */}
            {/* 
            <AnalyticsHeatmap
                data={dailyStats}
                selectedYear={selectedYear}
                onYearChange={setSelectedYear}
                customColors={customColors}
            /> 
            */}

            <Card sx={{ p: 2, mt: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Détail des interactions ({selectedYear})
                    </Typography>

                    <AnalyticsChart
                        data={chartStats}
                        isMobile={isMobile}
                        customColors={customColors}
                        onColorClick={handleColorClick}
                    />
                </CardContent>
            </Card>
            {/* Legend for color logic removed from here as it is handled in AnalyticsChart */}
        </PageLayout>
    );
};
